/**
 * Job sheet template data access (issue #188).
 *
 * Mirrors go-service/internal/api/job_sheet_templates.go: per-Company named
 * templates — a name plus fifteen boolean section toggles — with exactly one
 * default. The default swap happens server-side on PUT {is_default: true};
 * deleting the default or a referenced template is refused with a 409.
 */
import { apiFetch } from "../../../shared/api-client";

/** The job sheet sections a template can toggle — every part of the sheet
 * except the letterhead. A type alias, not an interface: the shared studio
 * shell reads any artifact's section map as `Record<string, boolean>`, and
 * only an alias carries the implicit index signature that allows. */
export type JobsheetSections = {
  schedule: boolean;
  pay: boolean;
  equipment: boolean;
  transport: boolean;
  contacts: boolean;
  access_notes: boolean;
  crew: boolean;
  event_details: boolean;
  task_summary: boolean;
  notes: boolean;
  event_date: boolean;
  call_time: boolean;
  location: boolean;
  site_contact: boolean;
  inventory: boolean;
};

export interface JobSheetTemplate {
  id: string;
  name: string;
  sections: JobsheetSections;
  is_default: boolean;
}

export interface JobSheetTemplatePayload {
  name: string;
  sections: JobsheetSections;
  /** PUT only; true promotes this template to the Company default. */
  is_default?: boolean;
}

/** Every section on — the shape new templates start from. */
export function fullDetailSections(): JobsheetSections {
  return {
    schedule: true,
    pay: true,
    equipment: true,
    transport: true,
    contacts: true,
    access_notes: true,
    crew: true,
    event_details: true,
    task_summary: true,
    notes: true,
    event_date: true,
    call_time: true,
    location: true,
    site_contact: true,
    inventory: true,
  };
}

/** List the Company's templates, default first then by name. */
export async function listJobSheetTemplates(
  signal?: AbortSignal,
): Promise<JobSheetTemplate[]> {
  const res = await apiFetch<JobSheetTemplate[]>(
    "/api/admin/job-sheet-templates",
    { signal },
  );
  return res ?? [];
}

/** Create a template. A duplicate name is a 409. */
export async function createJobSheetTemplate(
  payload: JobSheetTemplatePayload,
): Promise<JobSheetTemplate> {
  return apiFetch<JobSheetTemplate>("/api/admin/job-sheet-templates", {
    method: "POST",
    body: payload,
  });
}

/** Update name/sections, optionally promoting to default. */
export async function updateJobSheetTemplate(
  id: string,
  payload: JobSheetTemplatePayload,
): Promise<JobSheetTemplate> {
  return apiFetch<JobSheetTemplate>(`/api/admin/job-sheet-templates/${id}`, {
    method: "PUT",
    body: payload,
  });
}

/** Delete a template. The default or a referenced template is a 409. */
export async function deleteJobSheetTemplate(id: string): Promise<void> {
  await apiFetch<void>(`/api/admin/job-sheet-templates/${id}`, {
    method: "DELETE",
  });
}
